/**
 * One Business Discovery at a glance.
 *
 * INTERNAL, and exactly the page that must never be reachable by a client:
 * the L1-L8 stages, what has been established, and what was drawn from an
 * uploaded document are how the engine thinks, not how a conversation should
 * feel. Addressed by session id, which a client never holds.
 *
 * Reads existing endpoints only: the session record, its discovery status,
 * its document evidence and — when one has been generated — its report. The
 * detailed findings (business, technology, pain points, deep dive, outcomes)
 * exist only in the report, so without one this page says so instead of
 * guessing.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DiscoveryStage from '../components/DiscoveryStage';
import DocumentsPanel from '../components/DocumentsPanel';
import {
  fetchDiscovery,
  fetchDocumentEvidence,
  fetchReport,
  fetchSession,
  reportDocxDownloadUrl,
} from '../api/client';
import ConsoleLayout from './ConsoleLayout';
import {
  Badge,
  BulletList,
  Card,
  EmptyState,
  ErrorState,
  Facts,
  LoadingState,
  PageHeader,
  Prose,
  StatusBadge,
  SubHeading,
  Value,
} from './consoleUi';
import {
  REPORT_NOT_AVAILABLE,
  formatDateTime,
  formatDuration,
  friendlyError,
  progressOf,
  stageLabel,
} from './consoleData';
import '../styles/DiscoveryPage.css';

const BACK = { to: '/internal/discoveries', label: 'Business Discoveries' };

const EVIDENCE_TONES = {
  confirmed: 'ok',
  rejected: 'danger',
  pending: 'warn',
  unresolved: 'warn',
  outdated: 'neutral',
};

function isNotFound(result) {
  return result.status === 'rejected' && result.reason?.status === 404;
}

export default function DiscoveryPage() {
  const { sessionId } = useParams();
  const [data, setData] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setData({ status: 'loading' });

    Promise.allSettled([
      fetchSession(sessionId),
      fetchDiscovery(sessionId),
      fetchDocumentEvidence(sessionId),
      fetchReport(sessionId),
    ]).then(([session, discovery, evidence, report]) => {
      if (cancelled) return;
      if (isNotFound(session) && isNotFound(discovery)) {
        setData({ status: 'not-found' });
        return;
      }
      if (session.status === 'rejected' && discovery.status === 'rejected') {
        setData({ status: 'error', error: friendlyError(discovery.reason) });
        return;
      }
      setData({
        status: 'ready',
        session: session.status === 'fulfilled' ? session.value : null,
        discovery: discovery.status === 'fulfilled' ? discovery.value : null,
        discoveryError: discovery.status === 'rejected' ? friendlyError(discovery.reason) : null,
        // A session with no documents simply has no evidence.
        evidence: evidence.status === 'fulfilled' ? evidence.value.observations || [] : [],
        report: report.status === 'fulfilled' ? report.value : null,
        reportState: report.status === 'fulfilled' ? 'ready' : isNotFound(report) ? 'absent' : 'error',
      });
    });

    return () => { cancelled = true; };
  }, [sessionId]);

  if (data.status === 'loading') {
    return (
      <ConsoleLayout title="Business Discovery">
        <PageHeader back={BACK} eyebrow="Business Discovery" title="Loading…" />
        <Card><LoadingState label="Loading this Business Discovery…" /></Card>
      </ConsoleLayout>
    );
  }

  if (data.status === 'not-found' || data.status === 'error') {
    return (
      <ConsoleLayout title="Business Discovery">
        <PageHeader back={BACK} eyebrow="Business Discovery" title="Business Discovery unavailable" />
        <Card>
          {data.status === 'not-found' ? (
            <EmptyState
              title="Business Discovery not found"
              action={<Link to="/internal/discoveries" className="nsc-button nsc-button--secondary">Back to the list</Link>}
            >
              There is no Business Discovery with the reference {sessionId}.
            </EmptyState>
          ) : (
            <ErrorState title="This Business Discovery could not be loaded">{data.error}</ErrorState>
          )}
        </Card>
      </ConsoleLayout>
    );
  }

  const { session, discovery, discoveryError, evidence, report, reportState } = data;
  const company = discovery?.organization || report?.prospect?.organization?.value || null;
  const progress = progressOf(discovery, session?.current_layer);
  // Once a report exists, its own verdict decides complete or incomplete. The
  // stage count alone reads a normal close at L8 (7 of 8 recorded) as
  // unfinished, which contradicted the report card on the same page.
  const complete = report ? Boolean(report.interview_complete) : progress.complete;
  const ended = progress.closed || Boolean(report);
  const reportPath = `/internal/sessions/${sessionId}/report`;
  const transcriptPath = `/internal/sessions/${sessionId}/transcript`;

  return (
    <ConsoleLayout title={company || 'Business Discovery'}>
      <PageHeader
        back={BACK}
        eyebrow="Business Discovery"
        title={company || 'Company not provided'}
        subtitle={<span className="nsc-mono">Reference {sessionId}</span>}
        actions={
          <>
            {report && (
              <Link to={reportPath} className="nsc-button nsc-button--primary">View report</Link>
            )}
            <Link to={transcriptPath} className="nsc-button nsc-button--secondary">Transcript</Link>
          </>
        }
      >
        <div className="nsc-header-badges">
          {session && <StatusBadge lifecycle={session.lifecycle} />}
          {complete && <Badge tone="ok">Business Discovery complete</Badge>}
          {ended && !complete && <Badge tone="warn">Discovery incomplete</Badge>}
        </div>
      </PageHeader>

      {discoveryError && <p className="nsc-alert nsc-alert--danger" role="alert">{discoveryError}</p>}

      {ended && !complete && discovery && (
        <p className="nsc-alert nsc-alert--warn">
          Discovery incomplete: the conversation ended at {stageLabel(discovery.current_level)}
          {' '}with {discovery.completed_levels.length} of 8 stages complete.
        </p>
      )}

      <div className="nsc-detail">
        <div className="nsc-detail__main">
          <Card title="Company overview">
            <Facts
              items={[
                { label: 'Organisation', value: company },
                { label: 'Industry', value: discovery?.industry || report?.industry?.industry?.value },
                { label: 'Contact', value: report?.prospect?.contact_name?.value },
                { label: 'Role', value: discovery?.role || report?.prospect?.role?.value },
              ]}
            />
            {!report && (
              <p className="nsc-card__note">
                Contact and role come from the Business Discovery Report. {REPORT_NOT_AVAILABLE}.
              </p>
            )}
          </Card>

          <Card title="Primary problem">
            <Prose text={discovery?.primary_pain} />
            <div className="nsc-inline-meta">
              <span>Confirmed by the client:</span>
              {discovery?.problem_confirmed ? <Badge tone="ok">Yes</Badge> : <Badge tone="neutral">No</Badge>}
            </div>
          </Card>

          {report ? (
            <>
              <Card title="Key business information">
                <SubHeading>Business</SubHeading>
                <Prose text={report.business?.overview} />
                <div className="nsc-two-col">
                  <div>
                    <SubHeading>Products and services</SubHeading>
                    <BulletList items={report.business?.products_services} />
                  </div>
                  <div>
                    <SubHeading>Customers</SubHeading>
                    <BulletList items={report.business?.target_customers} />
                  </div>
                </div>
                <SubHeading>Technology and existing environment</SubHeading>
                <Prose text={report.technology_environment?.summary} />
                <div className="nsc-two-col">
                  <div>
                    <SubHeading>Tools in use</SubHeading>
                    <BulletList items={report.technology_environment?.tools} />
                  </div>
                  <div>
                    <SubHeading>Manual processes</SubHeading>
                    <BulletList items={report.technology_environment?.manual_processes} />
                  </div>
                </div>
              </Card>

              <Card title="Pain points">
                {!report.pain_points || report.pain_points.length === 0 ? (
                  <p className="nsc-empty-value">No problem was described during the conversation.</p>
                ) : (
                  <ul className="nsc-pains">
                    {report.pain_points.map((pain, index) => (
                      <li key={index} className="nsc-pain">
                        <p className="nsc-pain__title">{pain.problem}</p>
                        <div className="nsc-pain__badges">
                          {pain.confirmed && <Badge tone="ok">Confirmed</Badge>}
                          {!pain.specific && <Badge tone="neutral">General</Badge>}
                        </div>
                        {pain.evidence && <blockquote className="nsc-quote">“{pain.evidence}”</blockquote>}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card title="Deep dive">
                <Prose text={report.deep_dive?.summary} />
                <Facts
                  items={[
                    { label: 'Workflow', value: report.deep_dive?.workflow },
                    { label: 'Frequency', value: report.deep_dive?.frequency },
                    { label: 'Scale', value: report.deep_dive?.scale },
                    { label: 'Root cause', value: report.deep_dive?.root_cause },
                    { label: 'Workaround', value: report.deep_dive?.current_workaround },
                    { label: 'Urgency', value: report.deep_dive?.urgency },
                  ]}
                />
              </Card>

              <Card title="Problem statement">
                <div className="nsc-statement">
                  {report.confirmed_problem?.statement ? (
                    <blockquote className="nsc-quote nsc-quote--large">
                      “{report.confirmed_problem.statement}”
                    </blockquote>
                  ) : (
                    <Value value={null} />
                  )}
                  {report.confirmed_problem?.confirmed ? (
                    <Badge tone="ok">Confirmed by the client</Badge>
                  ) : (
                    <Badge tone="warn">Not confirmed</Badge>
                  )}
                </div>
              </Card>

              <Card title="Desired outcome">
                <BulletList items={report.desired_outcomes} empty="No desired outcome was stated." />
              </Card>
            </>
          ) : (
            <Card title="Detailed findings">
              {reportState === 'error' ? (
                <ErrorState title="The report could not be loaded">
                  Try again later, or open the report page.
                </ErrorState>
              ) : (
                <EmptyState
                  title={REPORT_NOT_AVAILABLE}
                  action={<Link to={reportPath} className="nsc-button nsc-button--secondary">Open report page</Link>}
                >
                  Business, technology, pain point, deep-dive and outcome details come
                  from the Business Discovery Report, which has not been generated for
                  this session.
                </EmptyState>
              )}
            </Card>
          )}

          {evidence.length > 0 && (
            <Card
              title="Document evidence"
              description="What a document said, and what the client made of it. A document never becomes a fact on its own."
            >
              <ul className="nsc-evidence">
                {evidence.map((item) => (
                  <li key={item.observation_id} className="nsc-evidence__item">
                    <div className="nsc-evidence__head">
                      <span className="nsc-evidence__file">{item.filename}</span>
                      <Badge tone={EVIDENCE_TONES[item.status] || 'neutral'}>{item.status}</Badge>
                    </div>
                    <blockquote className="nsc-quote">“{item.quote}”</blockquote>
                    <p className="nsc-evidence__meta">
                      Would fill <strong>{item.slot}</strong> with “{item.candidate}”
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <aside className="nsc-detail__side">
          <Card title="Discovery progress">
            {discovery ? (
              <DiscoveryStage discovery={discovery} />
            ) : (
              <EmptyState compact title="Progress not available">
                The discovery status could not be read.
              </EmptyState>
            )}
          </Card>

          <Card title="Report">
            {report ? (
              <div className="nsc-report-card">
                <div className="nsc-inline-meta">
                  {report.interview_complete ? (
                    <Badge tone="ok">Business Discovery complete</Badge>
                  ) : (
                    <Badge tone="warn">Discovery incomplete</Badge>
                  )}
                </div>
                <p className="nsc-card__note">Generated {formatDateTime(report.generated_at)}</p>
                <div className="nsc-button-row">
                  <Link to={reportPath} className="nsc-button nsc-button--primary">View report</Link>
                  <a href={reportDocxDownloadUrl(sessionId)} download className="nsc-button nsc-button--secondary">
                    Download DOCX
                  </a>
                </div>
              </div>
            ) : (
              <EmptyState
                compact
                title={reportState === 'error' ? 'Report could not be loaded' : REPORT_NOT_AVAILABLE}
                action={<Link to={reportPath} className="nsc-link">Open report page</Link>}
              />
            )}
          </Card>

          <Card title="Session">
            <Facts
              columns={1}
              items={[
                { label: 'Status', value: session ? <StatusBadge lifecycle={session.lifecycle} /> : null },
                { label: 'Created', value: formatDateTime(session?.created_at) },
                { label: 'Started', value: formatDateTime(session?.started_at) },
                { label: 'Completed', value: formatDateTime(session?.completed_at) },
                { label: 'Duration', value: formatDuration(session?.duration_seconds) },
                { label: 'Client turns', value: discovery ? String(discovery.turn_count) : null },
              ]}
            />
          </Card>

          <DocumentsPanel sessionId={sessionId} />
        </aside>
      </div>
    </ConsoleLayout>
  );
}
